/**
 * BriqueChart에서 재사용하기 위해 관리하는 Data
 *   - 이 Data는 Chart Element의 briqueChartData에 저장된다.
 *   - 일부 Method를 포함하고 있다.
 */
import Dayjs from 'dayjs'

export default {
  /**
   * 재사용하기 위한 값
   */
  briqueChartData: {
    xMin: 0, // X축 min값
    xMax: 0, // X축 max값
    yMin: 0, // Y축 min값
    yMax: 0, // Y축 max값

    xMinForPad: 0, // scale이 0일때의 X축 min값(여백 고려), layout.yaxis.range에는 scale에 따른 값이 계산되어 들어가므로 최초 min 값을 기록해 둔다.
    xMaxForPad: 0, // scale이 0일때의 X축 max값(여백 고려), layout.yaxis.range에는 scale에 따른 값이 계산되어 들어가므로 최초 min 값을 기록해 둔다.
    yMinForPad: 0, // scale이 0일때의 Y축 min값(여백 고려), layout.yaxis.range에는 scale에 따른 값이 계산되어 들어가므로 최초 min 값을 기록해 둔다.
    yMaxForPad: 0, // scale이 0일때의 Y축 max값(여백 고려), layout.yaxis.range에는 scale에 따른 값이 계산되어 들어가므로 최초 min 값을 기록해 둔다.
    maxValOfAllData: 0, // scale이 0일때의 max값, layout.yaxis.range에는 scale에 따른 값이 계산되어 들어가므로 최초 min 값을 기록해 둔다.
    tracesOriginal: [], // 샘플링 하기전의 초기 데이터
    layoutOriginal: {}, // 초기 Layout
    configOriginal: {}, // 초기 Config
    tracesOriginalAfterXAxisDataSelection: [], // X축 데이터 선택후 가공한 데이터 원본
    isSampling: false, // 샘플링 적용중
    isXAsixDataSelection: false, // X축 데이터 선택 적용중
    isDataNormalization: false, // Data Normalization 적용중

    selection: { // select시 Background Color를 지정
      rect: {
        shapeName: 'briquechart_selection_rect',
        visible: false,
        type: 'rect',
        x0: 0,
        x1: 0,
        y0: 0,
        y1: 0,
        line: {
          width: 0
        },
        fillcolor: 'rgba(128, 128, 128, 0.2)'
      }
    },
    selectedSeriesNameForContextMenu: '', // 차트내에서 마우스 우클릭시 Series Name
    seriesStyles: [], // series Style(예: {name: 'Series Name', mode: markers, yasix: 'y2'})
    csvData: [], // export csv data

    /**
     * Series의 Style을 초기화한다.(newPlot()시의 Style로 지정)
     * @param {Array} data 초기화 하려는 Style이 있는 Series Data
     */
    initSeriesStyles (data) {
      this.seriesStyles = []
      data.map((trace) => {
        this.seriesStyles.push({ name: trace.name, mode: trace.mode })
      })
    },

    /**
     * Series의 Style을 지정한다.
     * @param {*} seriesNameOrIndex Series Name 또는 Series Index
     * @param {*} attrObj Style 속성
     */
    setSeriesStyle (seriesNameOrIndex, attrObj) {
      if (typeof (seriesNameOrIndex) === 'string') {
        this.seriesStyles.some((seriesStyle) => {
          if (seriesStyle.name === seriesNameOrIndex) {
            _.merge(seriesStyle, attrObj)
            return true
          }
        })
      } else { // number
        _.merge(this.seriesStyles[seriesNameOrIndex], attrObj)
      }
    },

    /**
     * Series의 Style를 구한다.
     * @param {*} seriesNameOrIndex Series Name 또는 Series Index
     * @returns 시리즈의 Style
     */
    getSeriesStyle (seriesNameOrIndex) {
      if (typeof (seriesNameOrIndex) === 'string') {
        return this.seriesStyles.filter((seriesStyle) => {
          if (seriesStyle.name === seriesNameOrIndex) {
            return true
          }
        })[0]
      } else { // number
        return this.seriesStyles[seriesNameOrIndex]
      }
    },

    /**
     * 시리즈의 Color를 구한다.
     * @param {Object} element Chart Element
     * @param {String} seriesName Series Name
     * @returns Series의 Color
     */
    getSeriesColor (element, seriesName) {
      // 수동으로 지정된 Color
      let color = this.seriesStyles.filter(seriesStyle => {
        if (seriesStyle.name === seriesName) {
          return true
        }
      })[0]?.line?.color

      let index = element.data.map(item => item.name).indexOf(seriesName)
      let paletteColor = _.isEmpty(element.layout.colorway) ? '' : element.layout.colorway[index]

      return color ?? paletteColor
    },

    /**
     * 시리즈의 Marker Symbol을 구한다.
     * @param {Object} element Chart Element
     * @param {String} seriesName Series Name
     * @returns Series의 Marker Symbol
     */
    getSeriesMarkerSymbol (element, seriesName) {
      // 수동으로 지정된 Symbol
      let seriesStyle = this.seriesStyles.filter(seriesStyle => {
        if (seriesStyle.name === seriesName) {
          return true
        }
      })[0]

      let originalSeries = this.tracesOriginal.filter(series => {
        if (series.name === seriesName) {
          return true
        }
      })[0]

      if (seriesStyle && seriesStyle.mode) {
        return (seriesStyle.mode.includes('markers') && seriesStyle.marker) ? seriesStyle.marker.symbol : (originalSeries.marker?.symbol ? originalSeries.marker?.symbol : 'none')
      } else {
        return 'none'
      }
    },

    /**
     * 시리즈의 Marker Size를 구한다.
     * @param {Object} element Chart Element
     * @param {String} seriesName Series Name
     * @returns Series의 Marker Size
     */
    getSeriesMarkerSize (element, seriesName) {
      // 수동으로 지정된 Size
      let seriesStyle = this.seriesStyles.filter(seriesStyle => {
        if (seriesStyle.name === seriesName) {
          return true
        }
      })[0]

      let originalSeries = this.tracesOriginal.filter(series => {
        if (series.name === seriesName) {
          return true
        }
      })[0]

      return seriesStyle.marker?.size || originalSeries.marker?.size || 6
    },

    /**
     * 시리즈의 Chart Type을 구한다.
     * @param {Object} element Chart Element
     * @param {String} seriesName Series Name
     * @returns Series의 Chart Type
     */
    getSeriesChartType (element, seriesName) {
      let seriesStyle = this.seriesStyles.filter(seriesStyle => {
        if (seriesStyle.name === seriesName) {
          return true
        }
      })[0]

      if (seriesStyle && seriesStyle.type) {
        return seriesStyle.type
      } else {
        return 'none'
      }
    },

    /**
     * 시리즈의 Chart Mode를 구한다.
     * @param {Object} element Chart Element
     * @param {String} seriesName Series Name
     * @returns Series의 Chart Mode
     */
    getSeriesChartMode (element, seriesName) {
      let seriesStyle = this.seriesStyles.filter(seriesStyle => {
        if (seriesStyle.name === seriesName) {
          return true
        }
      })[0]

      if (seriesStyle && seriesStyle.mode) {
        return seriesStyle.mode
      } else {
        return 'none'
      }
    },

    /**
     * 시리즈의 Chart Fill을 구한다. (bar chart)
     * @param {Object} element Chart Element
     * @param {String} seriesName Series Name
     * @returns Series의 Chart Fill
     */
    getSeriesChartFill (element, seriesName) {
      let seriesStyle = this.seriesStyles.filter(seriesStyle => {
        if (seriesStyle.name === seriesName) {
          return true
        }
      })[0]

      if (seriesStyle && seriesStyle.fill) {
        return seriesStyle.fill
      } else {
        return 'none'
      }
    },

    /**
     * 시리즈의 Chart Orientation을 구한다. (vertical bar chart)
     * @param {Object} element Chart Element
     * @param {String} seriesName Series Name
     * @returns Series의 Chart Orientation
     */
    getSeriesChartOrientation (element, seriesName) {
      let seriesStyle = this.seriesStyles.filter(seriesStyle => {
        if (seriesStyle.name === seriesName) {
          return true
        }
      })[0]

      if (seriesStyle && seriesStyle.orientation) {
        return seriesStyle.orientation
      } else {
        return 'none'
      }
    },

    /**
     * 시리즈의 Chart Data 중 x를 구한다.
     * @param {*} element Chart Element
     * @param {*} seriesName Series Name
     * @returns Series의 Chart x values
     */
    getXValues (element, seriesName) {
      let tracesOriginal = this.tracesOriginal.filter(trace => {
        if (trace.name === seriesName) {
          return true
        }
      })[0]

      return tracesOriginal.x
    },

    /**
     * 시리즈의 Chart Data 중 y를 구한다.
     * @param {*} element Chart Element
     * @param {*} seriesName Series Name
     * @returns Series의 Chart y values
     */
    getYValues (element, seriesName) {
      let tracesOriginal = this.tracesOriginal.filter(trace => {
        if (trace.name === seriesName) {
          return true
        }
      })[0]

      return tracesOriginal.y
    },

    /**
     * Export Csv Data setting
     * @param {*} element chart element
     */
    setCsvData (element) {
      let ori = this.tracesOriginal
      let traces = _.cloneDeep(ori)

      let boxTypeCheck = false
      traces.forEach(c => {
        if (!boxTypeCheck && c.type === 'box') boxTypeCheck = true; return false
      })
      let exportData = []
      for (let trace of traces) {
        let seriesName = trace.name.replaceAll('.', '')

        let x = trace.x
        let y = trace.y
        for (let i = 0, max = y.length; i < max; i++) {
          let xData = boxTypeCheck ? i : x[i]
          if (xData instanceof Date) {
            xData = Dayjs(xData).format('YYYY-MM-DD HH:mm:ss.SSS')
          }
          let idx = exportData.findIndex(row => row.x === xData)
          if (idx < 0) {
            let row = {}
            row['x'] = xData
            row[seriesName] = y[i]
            exportData.push(row)
          } else {
            exportData[idx][seriesName] = y[i]
          }
        }
      }
      element.parentElement.__vue__.exportCsvData(exportData)
    }

    // removeSeriesStyleAttrs (seriesName, arrAttrs) {
    //   this.seriesStyles.some((seriesStyle) => {
    //     if (seriesStyle.name === seriesName) {
    //       seriesStyle = _.omit(seriesStyle, arrAttrs)
    //       return true
    //     }
    //   })
    // }

  }

}
